import type { StoreProcessTaskType } from '../enums/store-process-task-type.js'
import type { StoreTaskCompletionStatus } from '../enums/store-task-completion-status.js'

// GET /store/planning-week — planning de la semaine en cours de la boutique connectée
// (guard `store`). Alimente le widget « Planning semaine en cours » du dashboard boutique.

// Tranche horaire affichée par la grille : une ligne par heure de PLANNING_HOUR_START à
// PLANNING_HOUR_END. Les deux bornes sont partagées pour que le backend et la grille
// découpent la journée de la même façon. Journée complète minuit → minuit : la grille dépasse la
// hauteur de la colonne du dashboard, le widget défile verticalement (cf. `PlanningWeekCard`).
export const PLANNING_HOUR_START = 0
export const PLANNING_HOUR_END = 24

// Pointage d'un créneau, tel que la légende du widget le nomme.
export type StorePlanningClockStatus =
  | 'on_time' // pointé dans les temps
  | 'late' // pointé en retard — à valider
  | 'missing' // non pointé, alors que le créneau a commencé
  | 'upcoming' // créneau à venir : l'absence de pointage n'est pas une anomalie

export interface StorePlanningShift {
  id: number
  collaboratorId: number
  collaboratorName: string // « Prénom NOM »
  profilePictureUrl: string | null
  startTime: string // HH:mm (planifié)
  endTime: string // HH:mm (planifié, 24:00 possible pour une fin à minuit)
  origin?: 'planned' | 'manual-attendance'
  cancelledAt?: string | null
  clockStatus: StorePlanningClockStatus
  clockedInAt: string | null // HH:mm réel, null si non pointé
  clockedOutAt: string | null // HH:mm réel, null si toujours en poste
}

// Mode de couverture de la boutique sur une tranche de la journée.
export type StorePlanningCoverageKind =
  | 'presentiel' // un collaborateur est sur place
  | 'distanciel' // couverture à distance (créneau sur une boutique REMOTE)
  | 'ouverture' // boutique ouverte sans personnel affecté

export interface StorePlanningCoverageSegment {
  kind: StorePlanningCoverageKind
  startTime: string // HH:mm
  endTime: string // HH:mm
}

// Seules les livraisons ont une source en base aujourd'hui (logistic_files). Les autres blocs
// de la maquette (stocks, nettoyage, SAV) n'ont pas de modèle : le type est ouvert pour les
// accueillir sans casser le contrat.
export type StorePlanningEventKind = 'delivery'

export interface StorePlanningEvent {
  id: string // `${kind}-${id source}`
  kind: StorePlanningEventKind
  label: string
  startTime: string // HH:mm
  endTime: string // HH:mm
}

// Tâche « process » planifiée (store_process_tasks) : affichée en liste dans la carte PROCESS et
// en bloc sur sa plage horaire dans le widget planning.
export interface StorePlanningTask {
  id: number
  type: StoreProcessTaskType
  label: string // libellé FR du type (ex. « Nettoyage »)
  date: string // ISO date (business date Paris)
  startTime: string // HH:mm (heure planifiée)
  endTime: string // HH:mm (début + durée, borné à minuit)
  durationMinutes: number
  completed: boolean
  doneByCollabId: number | null // collaborateur ayant fait la tâche (null si non faite)
  doneByName: string | null // « Prénom NOM » du collaborateur, null si non faite / inconnu
  // Résultat de la validation : réalisée sans souci / avec problème (null tant que non validée).
  completionStatus: StoreTaskCompletionStatus | null
  issueComment: string | null // commentaire libre saisi en cas de problème (ou note optionnelle)
}

export interface StorePlanningDay {
  date: string // ISO date (business date Paris)
  weekday: number // 1 = lundi … 7 = dimanche
  isToday: boolean
  // Une valeur par tranche horaire, de PLANNING_HOUR_START à PLANNING_HOUR_END - 1 :
  // nombre de paniers validés sur l'heure.
  affluence: number[]
  coverage: StorePlanningCoverageSegment[]
  shifts: StorePlanningShift[]
  events: StorePlanningEvent[]
  tasks: StorePlanningTask[]
}

export interface StorePlanningWeekResponse {
  weekStart: string // lundi, ISO date
  weekEnd: string // dimanche, ISO date
  days: StorePlanningDay[] // 7 entrées, lundi → dimanche
  affluenceMax: number // max des affluences de la semaine, pour l'échelle de couleur
}

// GET /store/today-actions — alimente la barre d'actions du bas du dashboard boutique.
export interface StoreLastClock {
  firstName: string
  action: 'clock-in' | 'clock-out'
  clockedAt: string // HH:mm (heure de Paris)
  shiftStartTime: string // HH:mm planifié
  shiftEndTime: string // HH:mm planifié
}

export interface StoreTodayActionsResponse {
  // Dernier pointage enregistré sur la boutique aujourd'hui, `null` si personne n'a encore pointé.
  lastClock: StoreLastClock | null
}
